/**
 * DashboardTab
 *
 * Supervision summary metrics wired to real Medplum data.
 */

import { Card, SimpleGrid, Stack, Text } from '@mantine/core';
import { IconChartBar, IconFileCheck, IconClock, IconUsers } from '@tabler/icons-react';
import { useCosignQueueFromMedplum } from '../hooks/useCosignQueueFromMedplum';
import { useMissingDocumentsFromMedplum } from '../hooks/useMissingDocumentsFromMedplum';
import { useSupervisionScope } from '../hooks/useSupervisionScope';

export function DashboardTab(): JSX.Element {
  const { items: cosignItems, loading: cosignLoading } = useCosignQueueFromMedplum();
  const { items: ownUnsignedItems, loading: ownUnsignedLoading } = useCosignQueueFromMedplum({
    queue: 'own-unsigned',
  });
  const { items: missingDocItems, supervisees, loading: missingLoading } = useMissingDocumentsFromMedplum();
  const { role, superviseePractitionerIds } = useSupervisionScope();

  const isSupervisor = role === 'supervisor';
  const loading = cosignLoading || missingLoading || (isSupervisor && ownUnsignedLoading);

  const overdueDocuments = missingDocItems.filter((i) => i.status === 'overdue').length;
  const activeSupervisees = isSupervisor ? superviseePractitionerIds.length : undefined;

  const metrics = [
    ...(isSupervisor
      ? [{ label: 'Active Supervisees', value: activeSupervisees ?? 0, icon: IconUsers }]
      : []),
    ...(isSupervisor
      ? [{ label: 'Pending Co-sign', value: cosignItems.length, icon: IconFileCheck }]
      : [{ label: 'Unsigned Notes', value: cosignItems.length, icon: IconFileCheck }]),
    { label: 'Overdue Documents', value: overdueDocuments, icon: IconClock },
    ...(isSupervisor
      ? [{ label: 'Supervisee Compliance', value: supervisees.length > 0 ? `${supervisees.length} tracked` : '—', icon: IconChartBar }]
      : []),
    ...(isSupervisor
      ? [{ label: 'My Unsigned Notes', value: ownUnsignedItems.length, icon: IconFileCheck }]
      : []),
  ];

  return (
    <Stack gap="lg">
      <SimpleGrid cols={{ base: 1, sm: 2, md: metrics.length }}>
        {metrics.map((m) => (
          <Card key={m.label} withBorder p="lg">
            <Stack gap="xs" align="center">
              <m.icon size={24} stroke={1.5} color="var(--mantine-color-gray-5)" />
              <Text size="xl" fw={700} c={loading ? 'dimmed' : undefined}>
                {loading ? '—' : m.value}
              </Text>
              <Text size="xs" c="dimmed">{m.label}</Text>
            </Stack>
          </Card>
        ))}
      </SimpleGrid>
    </Stack>
  );
}
